/**
 * O rascunho de ambiente novo, do lado do formulário (wizard em modal, spec
 * 2026-10-09 §5). `GET`/`PUT /api/v1/tenants/{tenant_id}/stage-requests/{id}`
 * — `terraform_runner/formulario_routes.py`. O `PUT` manda só o campo mexido,
 * contra a `versao` lida; versão velha volta 409 com `{meu, gravado}` por campo.
 */
import axios from 'axios'
import { api } from '@/api/client'
import type { ResumoDaCriacaoDados } from '@/pages/chat/components/ResumoDaCriacao'

/** `diagrama`/`chamado`: o que o @Atendente tirou de lá ao pré-preencher (consoleai#25804). */
export type OrigemDoCampo = 'tenant' | 'padrao' | 'voce' | 'conversa' | 'diagrama' | 'chamado'

export interface Origem {
  origem: OrigemDoCampo
  motivo?: string
}

export interface Acompanhante {
  user_id: number
  nome: string
}

export interface CandidatoAAcompanhar extends Acompanhante {
  email: string
  gestor: boolean
}

export interface ServicoDoRascunho {
  nome?: string
  chave?: string
  papel?: string
  exposicao?: string
  porta?: number | string
  host?: string
  usa_banco?: string
  tipo_instancia?: string
  /** Serviços com o mesmo valor dividem a máquina (launch template, ASG e SG). */
  instancia?: string | null
  [campo: string]: unknown
}

export interface CamposDoRascunho {
  servicos?: ServicoDoRascunho[]
  banco?: Record<string, unknown>
  /** CloudHSM do stage: `modo` (FIPS, NON_FIPS ou nenhum) e `hsms` (2 a 3). */
  cloudhsm?: Record<string, unknown>
  acompanhantes?: number[]
  [campo: string]: unknown
}

export interface HostDoServico {
  servico: string
  caminho: string
  host: string | null
  /** `proposta`: a plataforma deriva (e refaz ao gravar); `seu`: escrito por alguém. */
  origem: 'proposta' | 'seu'
  fora_da_zona: boolean
}

/** Zona do ambiente e hosts finais — `formulario.dns`, as funções do molde. */
export interface DnsDoRascunho {
  ambiente: string | null
  dominio: string | null
  zona: string | null
  hosts: HostDoServico[]
  /** O domínio começa pelo ambiente: o sugerido é ele sem o ambiente. */
  dominio_com_ambiente: { sugerido: string; o_que_dizer: string } | null
}

export type EstadoDoAmbiente =
  | 'atual' | 'disponivel' | 'ausente' | 'em_criacao' | 'sincronizando'
  | 'sem_ou_do_projeto' | 'sem_acesso' | 'em_uso' | 'ilegivel'

/** Em que tenant o rascunho fica num ambiente — `ambientes_do_rascunho.opcao`. */
export interface OpcaoDeAmbiente {
  ambiente: string
  estado: EstadoDoAmbiente
  tenant: { id: number; nome: string } | null
  account_id: string | null
  /** Quando não dá para trocar: o que fazer, para gente ler. */
  caminho: string | null
}

/**
 * O cartão de criação pendente do rascunho, para QUEM VÊ (consoleai#26021):
 * `pode_confirmar` pelos mesmos portões do botão do cartão no chat, e o clique
 * vai ao confirm da mensagem `message_id`.
 */
export interface CartaoDeCriacao {
  message_id: number
  pode_confirmar: boolean
  motivo_bloqueio: string | null
}

export interface RascunhoDoFormulario {
  id: number
  versao: string
  estado: string
  projeto: string
  ambiente: string
  conversation_id: number | null
  tenant: { id: number; nome: string; account_id: string | null }
  organizacao: { id: number; nome: string; responsavel_user_id: number | null } | null
  campos: CamposDoRascunho
  origens: Record<string, Origem>
  /** `servicos[<chave>].host`, `dominio`… -> o valor que a plataforma propõe (o "voltar"). */
  propostas: Record<string, string>
  faltando: string[]
  invalidos: { campo: string; o_que_dizer: string }[]
  resumo: ResumoDaCriacaoDados | null
  acompanhantes: Acompanhante[]
  opcoes: Record<string, string[]>
  /** Mínimo e máximo dos números, pelo nome curto — os que o contrato aplica. */
  limites: Record<string, { minimo: number | null; maximo: number | null }>
  /** Os passos do wizard, na ordem: `formulario.PASSOS` no servidor. */
  passos: { chave: string; nome: string }[]
  dns: DnsDoRascunho
  /** `null` sem cartão pendente (rascunho incompleto); ausente em réplica antiga. */
  cartao_de_criacao?: CartaoDeCriacao | null
}

export interface GravacaoDoFormulario {
  versao: string
  campos_alterados?: Record<string, unknown>
  acompanhantes?: number[]
  voltar_a_proposta?: string[]
}

/** O corpo do 409: a versão de agora e, por campo em disputa, o meu e o gravado. */
export interface ConflitoDoFormulario {
  codigo: 'versao_divergente'
  versao: string
  campos: Record<string, { meu: unknown; gravado: unknown }>
}

export interface LeituraDaRede {
  estado: 'ok' | 'nao_conferido' | 'sem_valor'
  motivo?: string
  bloco_16?: string
  colisoes?: string[]
  visto_em?: string | null
}

const base = (tenantId: number, rascunhoId: number) =>
  `/v1/tenants/${tenantId}/stage-requests/${rascunhoId}`

/**
 * Lê o rascunho. Se o ambiente foi trocado, o rascunho mudou de tenant e o
 * cartão do chat ainda tem o velho: o servidor diz o novo (só a quem pode nele)
 * e a leitura segue para lá — `formulario_routes._carregar`.
 */
export async function lerRascunho(tenantId: number, rascunhoId: number): Promise<RascunhoDoFormulario> {
  try {
    return (await api.get(base(tenantId, rascunhoId))).data
  } catch (err) {
    const detalhe = axios.isAxiosError(err) ? err.response?.data?.detail : undefined
    const novo = detalhe?.codigo === 'rascunho_em_outro_tenant' ? Number(detalhe.tenant_id) : NaN
    if (Number.isFinite(novo) && novo !== tenantId) {
      return (await api.get(base(novo, rascunhoId))).data
    }
    throw err
  }
}

export const gravarRascunho = (
  tenantId: number, rascunhoId: number, corpo: GravacaoDoFormulario,
): Promise<RascunhoDoFormulario> =>
  api.put(base(tenantId, rascunhoId), corpo).then((r) => r.data)

export const lerRedeDoRascunho = (tenantId: number, rascunhoId: number): Promise<LeituraDaRede> =>
  api.get(`${base(tenantId, rascunhoId)}/rede`).then((r) => r.data)

export const lerAmbientesDoRascunho = (
  tenantId: number, rascunhoId: number,
): Promise<{ items: OpcaoDeAmbiente[] }> =>
  api.get(`${base(tenantId, rascunhoId)}/ambientes`).then((r) => r.data)

/** Troca o ambiente — e com ele o tenant e a conta. Sem conta, 409 com o caminho. */
export const trocarAmbienteDoRascunho = (
  tenantId: number, rascunhoId: number, corpo: { versao: string; ambiente: string },
): Promise<RascunhoDoFormulario> =>
  api.post(`${base(tenantId, rascunhoId)}/ambiente`, corpo).then((r) => r.data)

/** Zona e hosts finais para o domínio digitado (ou o gravado). Não grava. */
export const lerDnsDoRascunho = (
  tenantId: number, rascunhoId: number, dominio?: string,
): Promise<DnsDoRascunho> =>
  api.get(`${base(tenantId, rascunhoId)}/dns`, { params: dominio === undefined ? {} : { dominio } })
    .then((r) => r.data)

export const candidatosAAcompanhar = (
  tenantId: number, rascunhoId: number,
): Promise<{ items: CandidatoAAcompanhar[] }> =>
  api.get(`${base(tenantId, rascunhoId)}/acompanhantes`).then((r) => r.data)

export const descartarRascunho = (tenantId: number, rascunhoId: number, motivo: string) =>
  api.post(`${base(tenantId, rascunhoId)}/descartar`, { motivo }).then((r) => r.data)

/** Prontidão da conta (card 24897) — `terraform_runner/prontidao_routes.py`. */
export type EstadoDoItem = 'ok' | 'falta' | 'nao_conferido'

export interface ItemDaProntidao {
  nome: string
  rotulo: string
  estado: EstadoDoItem
  frase: string
  /** Só quando `estado === 'falta'`: o motor prepara, ou vira chamado. */
  conserto: 'preparar' | 'chamado' | null
}

export interface ProntidaoDaConta {
  itens: ItemDaProntidao[]
  bloqueia: string[]
  conferido_em: string
}

export interface PreparoDoItem {
  feito: boolean
  motivo: string | null
  faltando: string[]
  item: ItemDaProntidao
}

export const lerProntidao = (tenantId: number, rascunhoId: number): Promise<ProntidaoDaConta> =>
  api.get(`${base(tenantId, rascunhoId)}/prontidao`).then((r) => r.data)

export const prepararItem = (
  tenantId: number, rascunhoId: number, item: string,
): Promise<PreparoDoItem> =>
  api.post(`${base(tenantId, rascunhoId)}/prontidao/${item}/preparar`).then((r) => r.data)

export const abrirChamadoDoItem = (
  tenantId: number, rascunhoId: number, item: string,
): Promise<{ protocolo: string; item: ItemDaProntidao }> =>
  api.post(`${base(tenantId, rascunhoId)}/prontidao/${item}/chamado`).then((r) => r.data)

/** O valor gravado de um caminho (`servicos[portal].porta`, `banco.x`, `cloudhsm.x`, `dominio`). */
export function valorGravado(campos: CamposDoRascunho | undefined, caminho: string): unknown {
  if (!campos) return undefined
  const servico = /^servicos\[([^\]]+)\]\.(.+)$/.exec(caminho)
  if (servico) {
    const [, chave, campo] = servico
    const s = (campos.servicos ?? []).find(
      (x) => String(x.chave ?? x.nome ?? '').toLowerCase() === chave.toLowerCase(),
    )
    return s?.[campo]
  }
  if (caminho.startsWith('banco.')) return campos.banco?.[caminho.slice('banco.'.length)]
  if (caminho.startsWith('cloudhsm.')) return campos.cloudhsm?.[caminho.slice('cloudhsm.'.length)]
  return campos[caminho]
}
